import * as THREE from 'three';
import { getSectorJourney } from './sectorJourney';

export const SECTOR_ENVIRONMENTS = [
  'ocean-moons',
  'crystal-belt',
  'binary-embers',
  'orbital-relay',
  'molten-gate',
  'black-hole',
  'rift-ribbons',
  'living-nebula',
  'shattered-constellation',
  'starless-heart',
];

// A seeded sky stays recognizable after reload. Each destination has its own
// geometry as well as palette; all scenery stays beyond the playable board.
export const createSectorEnvironment = (sector = 1) => {
  const journey = getSectorJourney(sector);
  const group = new THREE.Group();
  group.name = `sector-${journey.sector}-${
    SECTOR_ENVIRONMENTS[journey.sector - 1]
  }`;
  const primary = journey.style['--sector-primary'];
  const secondary = journey.style['--sector-secondary'];
  let seed = journey.sector * 491 + 17;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const moving = [];
  const material = (color, opacity = 0.6) =>
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  const mesh = (geometry, color, position, scale = 1, opacity = 0.6) => {
    const object = new THREE.Mesh(geometry, material(color, opacity));
    object.position.set(...position);
    object.scale.setScalar(scale);
    group.add(object);
    return object;
  };
  const ring = (radius, thickness, color, position, tilt = 0) => {
    const object = mesh(
      new THREE.TorusGeometry(radius, thickness, 6, 64),
      color,
      position
    );
    object.rotation.set(tilt, 0.2, 0.3);
    return object;
  };
  const globe = (radius, color, position) =>
    mesh(new THREE.SphereGeometry(radius, 24, 16), color, position, 1, 0.65);
  const crystal = (position, size, color) => {
    const object = mesh(
      new THREE.OctahedronGeometry(size),
      color,
      position,
      1,
      0.38
    );
    object.scale.y = 1.8;
    object.rotation.set(random() * 3, random() * 3, random() * 3);
    moving.push(object);
    return object;
  };

  const count = journey.sector === 10 ? 36 : 170 + journey.sector * 13;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    positions[i * 3] = (random() - 0.5) * 90;
    positions[i * 3 + 1] = (random() - 0.35) * 55;
    positions[i * 3 + 2] = -22 - random() * 32;
  }
  const stars = new THREE.BufferGeometry();
  stars.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const starField = new THREE.Points(
    stars,
    new THREE.PointsMaterial({
      color:
        journey.sector === 3
          ? '#ffd2a0'
          : journey.sector === 8
          ? '#a6ffd2'
          : '#cceeff',
      size: journey.sector === 2 ? 0.14 : 0.075,
      transparent: true,
      opacity: journey.sector === 10 ? 0.15 : 0.65,
      depthWrite: false,
    })
  );
  group.add(starField);

  const sky = mesh(
    new THREE.SphereGeometry(75, 20, 12),
    journey.style['--sector-sky'],
    [0, 0, 0],
    1,
    0.3
  );
  sky.material.side = THREE.BackSide;
  switch (journey.sector) {
    case 1:
      globe(7, '#164b69', [-17, 7, -36]);
      ring(10, 0.17, '#74cfe6', [-17, 7, -36], 1.1);
      globe(1.5, '#6fa1ba', [-5, 12, -32]);
      globe(0.8, '#a6cbd0', [-24, 16, -32]);
      break;
    case 2:
      for (let i = 0; i < 24; i++)
        crystal(
          [-30 + i * 2.6, Math.sin(i * 0.65) * 5 + 6, -30 - random() * 15],
          0.6 + random() * 1.7,
          i % 2 ? '#6fcce4' : '#9d7bea'
        );
      break;
    case 3:
      globe(5, '#e17139', [-15, 13, -38]);
      globe(3, '#ebbd66', [-5, 17, -40]);
      ring(7.3, 0.24, '#ffb470', [-15, 13, -38]);
      for (let i = 0; i < 15; i++)
        crystal(
          [(random() - 0.5) * 58, -4 + random() * 10, -28 - random() * 15],
          0.3 + random() * 0.6,
          '#a96147'
        );
      break;
    case 4:
      for (let i = 0; i < 4; i++) {
        const relay = ring(
          6 + i * 2.2,
          0.12,
          i % 2 ? '#5c8cc0' : '#74d8d2',
          [18, 9, -35],
          0.6 + i * 0.3
        );
        moving.push(relay);
      }
      mesh(new THREE.IcosahedronGeometry(2, 0), '#284c68', [18, 9, -35]);
      break;
    case 5:
      for (let i = 0; i < 16; i++) {
        const shard = crystal(
          [Math.sin(i * 1.9) * 3, -16 + i * 2.5, -33],
          1 + random() * 2,
          '#e79149'
        );
        shard.scale.x = 0.3;
      }
      ring(12, 0.32, '#d88b42', [0, 8, -38], 0.3);
      break;
    case 6:
      globe(5, '#01020a', [-15, 10, -34]);
      for (let i = 0; i < 5; i++)
        ring(
          5.5 + i * 0.75,
          0.14,
          i % 2 ? '#f5b37e' : '#a75268',
          [-15, 10, -34],
          1.15
        );
      break;
    case 7:
      for (let i = 0; i < 7; i++) {
        const points = Array.from(
          { length: 9 },
          (_, j) =>
            new THREE.Vector3(
              -30 + j * 8,
              4 + Math.sin(j * 0.8 + i) * 5 + i,
              -32 - i * 1.8
            )
        );
        const ribbon = mesh(
          new THREE.TubeGeometry(
            new THREE.CatmullRomCurve3(points),
            48,
            0.08 + i * 0.025,
            5,
            false
          ),
          i % 2 ? '#767de3' : '#aa62ce',
          [0, 0, 0]
        );
        moving.push(ribbon);
      }
      break;
    case 8:
      globe(7, '#1d443e', [19, 8, -36]);
      for (let i = 0; i < 12; i++) {
        const angle = i * 2.399;
        globe(0.25 + random() * 0.7, i % 2 ? '#8ee5bc' : '#b0c76a', [
          19 + Math.cos(angle) * (8 + random() * 4),
          8 + Math.sin(angle) * 9,
          -30 - random() * 10,
        ]);
      }
      ring(11, 0.13, '#6ca98b', [19, 8, -36], 0.9);
      break;
    case 9:
      for (let i = 0; i < 9; i++) {
        const angle = i * 0.65;
        crystal(
          [Math.cos(angle) * 16, 8 + Math.sin(angle) * 12, -35],
          1 + random() * 2,
          i % 2 ? '#c28ac0' : '#8b86de'
        );
      }
      break;
    default:
      for (let i = 0; i < 3; i++) {
        const orbit = ring(
          9 + i * 3,
          0.07,
          i % 2 ? '#53366e' : '#28557c',
          [0, 6, -39],
          i * 0.6
        );
        moving.push(orbit);
      }
      globe(6, '#01030b', [0, 6, -39]);
  }
  group.userData.environment = SECTOR_ENVIRONMENTS[journey.sector - 1];
  group.userData.primary = primary;
  group.userData.secondary = secondary;
  return {
    group,
    sector: journey.sector,
    update: (time, reduced = false) =>
      moving.forEach((object, index) => {
        if (!reduced)
          object.rotation.z =
            time * (index % 2 ? 0.008 : -0.006) + index * 0.37;
      }),
    dispose: () =>
      group.traverse((object) => {
        object.geometry?.dispose();
        object.material?.dispose();
      }),
  };
};
